import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import style from "./Landing.module.css";

import Container from "@/layouts/container/Container";
import CourseSwiper from "@/features/courses/components/courseSwiper/CourseSwiper";
import IncompleteCourses from "@/features/courses/components/incompleteCourses/IncompleteCourses";
import ChoosePlan from "@/features/auth/components/choosePlan/ChoosePlan";

import LandingHero from "@/features/landing/components/landingHero/LandingHero";
import CategoryWheel from "@/features/landing/components/categoryWheel/CategoryWheel";
import InstructorGrid from "@/features/landing/components/instructorGrid/InstructorGrid";
import TestimonialMarquee from "@/features/landing/components/testimonialMarquee/TestimonialMarquee";
import FaqAccordion from "@/features/landing/components/faqAccordion/FaqAccordion";
import RegisterPanel from "@/features/landing/components/registerPanel/RegisterPanel";
import ContinueHero from "@/features/landing/components/continueHero/ContinueHero";
import UpsellBanner from "@/features/landing/components/upsellBanner/UpsellBanner";

import { useFreeCourses } from "@/features/courses/hooks/useFreeCourses";
import { useGetMostWatchedCourses } from "@/features/courses/hooks/useGetMostWatchedCourses";
import { useGetRecentCourses } from "@/features/courses/hooks/useGetRecentCourses";
import { deriveCategoryRails } from "@/features/courses/deriveCategoryRails";

function Landing({ mode }) {
	const { t } = useTranslation();
	const { courses, freeCourses, lockedCount, isPending } = useFreeCourses();
	const { data: mostWatched } = useGetMostWatchedCourses();
	const { data: recentCourses } = useGetRecentCourses();

	// A free account gets the same shelves a subscriber gets. What separates
	// the two is what the cards unlock, not which rails exist.
	const rails = useMemo(() => deriveCategoryRails(courses), [courses]);

	if (mode === "free") {
		return (
			<div className={style.page}>
				<ContinueHero />

				<Container>
					{freeCourses.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.free.title")}
								/* Not the guest page's line: this reader
								   already has the account that one asks for. */
								description={t(
									"landing:page.free.descriptionMember"
								)}
								courses={freeCourses}
							/>
						</div>
					)}

					{lockedCount > 0 && (
						<UpsellBanner lockedCount={lockedCount} />
					)}

					{recentCourses?.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.recent.title")}
								description={t("landing:page.recent.description")}
								courses={recentCourses}
							/>
						</div>
					)}

					<div className={style.swiperBlock}>
						<IncompleteCourses />
					</div>

					{mostWatched?.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.mostWatched.title")}
								description={t(
									"landing:page.mostWatched.description"
								)}
								courses={mostWatched}
							/>
						</div>
					)}

					{rails.biznis.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.bestIn.biznis")}
								courses={rails.biznis}
							/>
						</div>
					)}

					{rails.tehnologija.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.bestIn.tehnologija")}
								courses={rails.tehnologija}
							/>
						</div>
					)}

					{rails.kreativa.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.bestIn.kreativa")}
								courses={rails.kreativa}
							/>
						</div>
					)}

					{rails.audio.length > 0 && (
						<div className={style.swiperBlock}>
							<CourseSwiper
								title={t("landing:page.audio.title")}
								description={t("landing:page.audio.description")}
								courses={rails.audio}
							/>
						</div>
					)}
				</Container>
			</div>
		);
	}

	return (
		<div className={style.page}>
			<LandingHero />

			<Container>
				{freeCourses.length > 0 && (
					<div className={style.swiperBlock}>
						<CourseSwiper
							title={t("landing:page.free.title")}
							description={t("landing:page.free.descriptionGuest")}
							courses={freeCourses}
						/>
					</div>
				)}

				{recentCourses?.length > 0 && (
					<div className={style.swiperBlock}>
						<CourseSwiper
							title={t("landing:page.recent.title")}
							description={t("landing:page.recent.description")}
							courses={recentCourses}
						/>
					</div>
				)}

				{mostWatched?.length > 0 && (
					<div className={style.swiperBlock}>
						<CourseSwiper
							title={t("landing:page.mostWatched.title")}
							description={t(
								"landing:page.mostWatched.description"
							)}
							courses={mostWatched}
						/>
					</div>
				)}
			</Container>

			<CategoryWheel courses={courses} isPending={isPending} />
			<InstructorGrid courses={courses} />

			<div id="cijene" className={style.pricing}>
				<Container size="mini">
					<ChoosePlan />
				</Container>
			</div>

			<TestimonialMarquee />

			<section id="registracija" className={style.faqSection}>
				<Container>
					<div className={style.faqGrid}>
						<RegisterPanel />
						<FaqAccordion />
					</div>
				</Container>
			</section>
		</div>
	);
}

export default Landing;
